import * as React from 'react';
import * as Component from './Components';
import HistoryRowControls from 'Components/HistoryRowControls';
import type { IHistoryContext } from 'Containers/FormContextHandler';
import { withHistoryContext } from 'Containers/FormContextHandler';
import { isPartialDate, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import { DataContext } from './Components/context';
import { filterByDocumentType } from 'Utility/documentHandling';

const headerText = (d: IEpilepsySurgicalTreatment, fm: (id: string) => string): string => {
  const date = d.date ? formatPartialDate(d.date) + ' - ' : '';
  const typeOfOperationFirst = d.operations?.[0]?.typeOfOperation
    ? fm(`surgicalTreatment.operation.typeOfOperation.opts.${d.operations[0].typeOfOperation}`)
    : '';
  const typeOfOperationSecond = d.operations?.[1]?.typeOfOperation
    ? ' / ' + fm(`surgicalTreatment.operation.typeOfOperation.opts.${d.operations[1].typeOfOperation}`)
    : '';
  return date + typeOfOperationFirst + typeOfOperationSecond;
};

const SurgicalTreatmentHistoryRow = ({ startEdit, documents, fm }: IHistoryContext): React.JSX.Element => {
  const locPath = 'surgicalTreatment';
  return (
    <React.Fragment>
      {startEdit && documents && documents.length > 0
        ? filterByDocumentType(documents, 'epilepsySurgicalTreatment')
            .toSorted((a, b) => sortPartialDate(b.date, a.date))
            .map(d => {
              const uncededCompication =
                d.complications?.filter(comp => !isPartialDate(comp.complicationCededDate)) ?? [];
              const cededCompication = d.complications?.filter(comp => isPartialDate(comp.complicationCededDate)) ?? [];
              return (
                <React.Fragment key={d._id}>
                  <HistoryRow
                    headerText={headerText(d, fm)}
                    rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
                    controlsMargin={false}
                  >
                    <React.Fragment>
                      {d.operations
                        ? d.operations.map(o => (
                            <DataContext.Provider key={o.id} value={{ d: o, fm }}>
                              <Component.Operations />
                            </DataContext.Provider>
                          ))
                        : ''}
                      {d.pads ? (
                        <React.Fragment>
                          <HistoryRowSubHeader header={fm(`${locPath}.pad.header`)} />
                          {d.pads.map(p => (
                            <DataContext.Provider key={p.id} value={{ d: p, fm }}>
                              <Component.Pads />
                            </DataContext.Provider>
                          ))}
                        </React.Fragment>
                      ) : (
                        ''
                      )}
                      {Array.isArray(uncededCompication) && uncededCompication.length > 0 ? (
                        <React.Fragment>
                          <HistoryRowSubHeader header={fm(`${locPath}.complications.header`)} />
                          {uncededCompication
                            .toSorted((a, b) => sortPartialDate(a.date, b.date))
                            .map(c => (
                              <DataContext.Provider key={c.id} value={{ d: c, fm }}>
                                <Component.Complications />
                              </DataContext.Provider>
                            ))}
                        </React.Fragment>
                      ) : (
                        ''
                      )}
                      {Array.isArray(cededCompication) && cededCompication.length > 0 ? (
                        <React.Fragment>
                          <HistoryRowSubHeader header={fm(`${locPath}.complications.cededComplications`)} />
                          {cededCompication
                            .toSorted((a, b) => sortPartialDate(a.date, b.date))
                            .map(c => (
                              <DataContext.Provider key={c.id} value={{ d: c, fm }}>
                                <Component.Complications />
                              </DataContext.Provider>
                            ))}
                        </React.Fragment>
                      ) : (
                        ''
                      )}
                      {Array.isArray(d.engelClassification) && d.engelClassification.length > 0 ? (
                        <React.Fragment>
                          <HistoryRowSubHeader header={fm(`${locPath}.engelClassification.header`)} />
                          {d.engelClassification
                            .toSorted((a, b) => sortPartialDate(a.date, b.date))
                            .map(e => (
                              <DataContext.Provider key={e.id} value={{ d: e, fm }}>
                                <Component.EngelClassification />
                              </DataContext.Provider>
                            ))}
                        </React.Fragment>
                      ) : (
                        ''
                      )}
                    </React.Fragment>
                  </HistoryRow>
                </React.Fragment>
              );
            })
        : null}
    </React.Fragment>
  );
};

export default withHistoryContext(SurgicalTreatmentHistoryRow);
